<template>
  <v-container>
    <!-- Hero Section -->
    <v-row justify="center" class="hero mb-5">
      <v-col cols="12" md="8" class="text-center">
        <h1 class="display-1 text-secondary">Holonet Galactic Encyclopedia</h1>
        <p class="text-h6 text-white">
          Explore the vast galaxy of Star Wars. Dive into detailed information about planets, regions, and an
          ever-growing collection of lore, all powered by Wikidata.
        </p>
      </v-col>
    </v-row>

    <!-- Stats Section -->
    <v-row justify="center" class="stats mb-5">
      <v-col cols="12" sm="6" md="4" class="text-center" v-if="stats">
        <v-card outlined>
          <v-card-title class="text-h4 text-secondary"><span ref="planetCountRef"></span></v-card-title>
          <v-card-subtitle class="text-h5 text-white">Planets</v-card-subtitle>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4" class="text-center">
        <v-card outlined>
          <v-card-title class="text-h4 text-secondary"><span ref="regionCountRef"></span></v-card-title>
          <v-card-subtitle class="text-h5 text-white">Regions and Systems</v-card-subtitle>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4" class="text-center">
        <v-card outlined>
          <v-card-title class="text-h4 text-secondary"><span ref="characterCountRef"></span></v-card-title>
          <v-card-subtitle class="text-h5 text-white">Characters</v-card-subtitle>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4" class="text-center">
        <v-card outlined>
          <v-card-title class="text-h4 text-secondary"><span ref="populationCountRef"></span></v-card-title>
          <v-card-subtitle class="text-h5 text-white">Galaxy-Wide Population</v-card-subtitle>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4" class="text-center" v-if="stats">
        <v-card outlined>
          <v-card-title class="text-h4 text-secondary"><span ref="workCountRef"></span></v-card-title>
          <v-card-subtitle class="text-h5 text-white">Star Wars Lore Sources</v-card-subtitle>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4" class="text-center" v-if="stats">
        <v-card outlined>
          <v-card-title class="text-h4 text-secondary"><span ref="factCountRef"></span></v-card-title>
          <v-card-subtitle class="text-h5 text-white">Facts Aggregated</v-card-subtitle>
        </v-card>
      </v-col>

    </v-row>

    <!-- Call to Action -->
    <v-row justify="center" class="mb-5">
      <v-col cols="12" md="8" class="text-justify">
        <p class="text-body-1">
          The Holonet Galactic Encyclopedia is your gateway to exploring the vast galaxy of Star Wars. Discover detailed
          information about planets, galactic regions, and the lore that connects them.
          Dive into the rich data sourced from Wikidata and contribute to expanding the knowledge base.
        </p>
        <p class="text-body-1 mt-2">
          <strong>Planets</strong>: This section allows you to explore data about the planets in the Star Wars galaxy.
          Discover the locations, history, and key features of iconic planets such as Tatooine, Naboo, and many others.
        </p>
        <v-col cols="12" md="12" class="text-center">
          <v-btn color="secondary" large to="/planets/atlas">Explore Planets</v-btn>
        </v-col>
        <p class="text-body-1">
          <strong>SPARQL</strong>: Here, you’ll find access to sample queries over the Wikidata Star Wars
          dataset. Whether you’re a developer, data enthusiast, or researcher, you can try out various SPARQL queries to
          explore the galaxy’s data in more depth.
        </p>
        <p class="text-body-1 mt-2">
          Want to be a part of this galactic journey? There are many ways to get involved.
          Explore our documentation to understand how the project works and what is the roadmap, or start contributing
          your knowledge today!
        </p>
        <v-col cols="12" md="12" class="text-center">
          <v-btn color="secondary" large to="/contribute">Contribute</v-btn>
          <v-btn color="secondary" large to="/about" class="ml-4">Learn More</v-btn>
        </v-col>
        <News />
      </v-col>
    </v-row>
    
    <!-- Cite this work -->
    <v-row justify="center">
      <v-col cols="12" md="8" class="text-justify">
        <h2 class="display-2 text-center text-secondary">Cite this work</h2>
        <p class="text-body-1">
          Yoan Chabot (2025). Holonet Galactic Encyclopedia. Retrieved from https://sw-holonet.com/
        </p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref, onMounted } from "vue";
import apiClient from '@/apiClient';
import { CountUp } from "countup.js";
import News from '@/components/News.vue';
import { useHead } from '@unhead/vue';

export default {
  name: "Home",
  components: {
    News, 
  },
  setup() {
    const stats = ref([]);
    const planetCountRef = ref(null);
    const regionCountRef = ref(null);
    const populationCountRef = ref(null);
    const workCountRef = ref(null);
    const factCountRef = ref(null);
    const characterCountRef = ref(null);

    useHead({
      title: "Holonet – Explore the Star Wars Galaxy with Wikidata-Powered Lore",
      meta: [
        {
          name: "description",
          content: "Discover the Star Wars galaxy with Holonet Galactic Encyclopedia. Explore detailed data on planets, regions, and lore, all powered by Wikidata. Contribute and expand the ultimate Star Wars knowledge base!",
        },
        {
          property: "og:title",
          content: "Holonet – Explore the Star Wars Galaxy with Wikidata-Powered Lore",
        },
        {
          property: "og:description",
          content: "Dive into the Star Wars universe with Holonet. Access structured data on planets, regions, and lore, sourced from Wikidata. Join the community and contribute to the galaxy’s knowledge base.",
        },
        {
          property: "og:url",
          content: "https://sw-holonet.com",
        },
        { property: "og:type", content: "website" },
        {
          name: "twitter:title",
          content: "Holonet – Explore the Star Wars Galaxy with Wikidata",
        },
        {
          name: "twitter:description",
          content: "Explore the Star Wars galaxy with Holonet’s detailed data on planets, regions, and lore, powered by Wikidata. Contribute and expand the knowledge base today!",
        },
        { name: "twitter:card", content: "summary" },
        {
          name: "keywords",
          content: "Holonet, Star Wars encyclopedia, Star Wars galaxy, Wikidata Star Wars, Star Wars lore, Star Wars planets, Star Wars regions, Star Wars knowledge base, contribute to Star Wars data",
        },
      ],
    });

    // Fetch stats  from the backend
    onMounted(() => {
      // Fetch data from backend
      apiClient.get("/api/statistics")
        .then(response => {
          stats.value = response.data;
          console.trace(stats.value);

          // Trigger CountUp.js animation on stats update
          triggerCountUp();
        })
        .catch(error => {
          console.error("Error fetching statistics:", error);
        });
    });

    // Function to trigger CountUp.js animations for each stat
    const triggerCountUp = () => {
      if (stats.value) {
        new CountUp(planetCountRef.value, stats.value.planet_count, {
          duration: 2,
          useEasing: true,
        }).start();
        new CountUp(regionCountRef.value, stats.value.region_count, {
          duration: 2,
          useEasing: true,
        }).start();
        new CountUp(populationCountRef.value, stats.value.galaxy_population, {
          duration: 10,
          useEasing: true,
        }).start();
        new CountUp(workCountRef.value, stats.value.work_count, {
          duration: 2,
          useEasing: true,
        }).start();
        new CountUp(factCountRef.value, stats.value.fact_count, {
          duration: 5,
          useEasing: true,
        }).start();
        new CountUp(characterCountRef.value, stats.value.character_count, {
          duration: 5,
          useEasing: true,
        }).start();
      }
    };

    return {
      stats,
      planetCountRef,
      regionCountRef,
      populationCountRef,
      workCountRef,
      factCountRef,
      characterCountRef
    };
  },
};
</script>

<style scoped>
.hero {
  background: url('@/assets/bg_space_seamless.png') no-repeat center center;
  background-size: cover;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
}

.hero h1 {
  font-family: 'Star Wars Font', sans-serif;
}

.stats {
  margin-top: 20px;
}

@media (max-width: 600px) {
  .hero {
    min-height: 200px;
    padding: 10px;
  }

  .hero h1 {
    font-size: 2rem;
  }

  .hero p {
    font-size: 1rem;
  }
}
</style>
